/**
 * Game catalog helpers used by the canonical home screen.
 */

import {
  createGamePlatformApiClient,
  type GameManifest,
} from "@cloudnest/board-game-runtime";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { getGameType } from "../game-registry";
import { buildLobbyPath, navigateTo } from "../router";
import { getConfig } from "../shared/config/runtime-config";
import {
  buildGameCatalogEntryTestId,
  formatObserverSupportLabel,
  formatPlayerCountLabel,
  formatRulesetLabel,
  formatRuntimeLabel,
} from "./game-catalog-model";
import {
  type CatalogEntry,
  DOC_AXES,
  emptyGameDocsIndex,
  type GameDocLinks,
  type GameDocsIndex,
  hasAnyDocLink,
  mergeCatalogWithDocs,
} from "./game-docs";

export type CatalogLoadState =
  | { readonly status: "loading"; readonly games: readonly GameManifest[] }
  | { readonly status: "ready"; readonly games: readonly GameManifest[] }
  | {
      readonly status: "error";
      readonly games: readonly GameManifest[];
      readonly error: string;
    };

const initialCatalogState: CatalogLoadState = {
  status: "loading",
  games: [],
};

interface GameCatalogListProps {
  readonly games: readonly GameManifest[];
  readonly showDetails?: boolean;
  readonly entryTestIdBuilder?: (gameType: string) => string;
  readonly emptyMessage?: string;
  readonly docsIndex?: GameDocsIndex;
}

export function useGameCatalog(): CatalogLoadState {
  const [catalog, setCatalog] = useState<CatalogLoadState>(initialCatalogState);

  const catalogClient = useMemo(
    () =>
      createGamePlatformApiClient({
        baseUrl: getConfig().multiplayerServiceUrl,
      }),
    [],
  );

  useEffect(() => {
    let active = true;

    async function loadCatalog() {
      setCatalog(initialCatalogState);
      try {
        const response = await catalogClient.getCatalog();
        if (!active) return;
        if (response.success) {
          setCatalog({ status: "ready", games: response.data.games });
          return;
        }
        setCatalog({
          status: "error",
          games: [],
          error: response.error || "게임 목록을 불러올 수 없습니다",
        });
      } catch (error) {
        if (!active) return;
        setCatalog({
          status: "error",
          games: [],
          error:
            error instanceof Error
              ? error.message
              : "게임 목록을 불러올 수 없습니다",
        });
      }
    }

    void loadCatalog();
    return () => {
      active = false;
    };
  }, [catalogClient]);

  return catalog;
}

export function GameCatalogList({
  games,
  showDetails = false,
  entryTestIdBuilder = buildGameCatalogEntryTestId,
  emptyMessage = "표시할 게임이 없습니다.",
  docsIndex = emptyGameDocsIndex,
}: GameCatalogListProps) {
  const entries = mergeCatalogWithDocs(games, docsIndex);

  if (entries.length === 0) {
    return <p className="text-sm text-text-secondary">{emptyMessage}</p>;
  }

  return (
    <ul className="flex list-none flex-col gap-3 p-0">
      {entries.map((entry) => (
        <li
          key={entry.gameType}
          className="overflow-hidden rounded-lg border border-border-subtle bg-surface-secondary"
        >
          {entry.manifest ? (
            <button
              type="button"
              data-testid={entryTestIdBuilder(entry.gameType)}
              onClick={() => navigateTo(buildLobbyPath(entry.gameType, "v2"))}
              className="flex w-full cursor-pointer items-start gap-3 p-4 text-left transition-colors hover:bg-surface-tertiary"
            >
              <GameEntrySummary entry={entry} showDetails={showDetails} />
            </button>
          ) : (
            <div
              data-testid={`game-entry-docs-only-${entry.gameType}`}
              className="flex w-full items-start gap-3 p-4 text-left"
            >
              <GameEntrySummary entry={entry} showDetails={showDetails} />
            </div>
          )}
          {hasAnyDocLink(entry.docs) ? (
            <GameDocShortcuts gameType={entry.gameType} docs={entry.docs} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Document shortcuts sit outside the entry button — a link cannot be nested
 * inside a button — so they stay reachable even when the game itself is not.
 * The chip carries the affordance (border, surface, cursor); the mark is
 * decorative and never the only signal that the chip is actionable.
 */
function GameDocShortcuts({
  gameType,
  docs,
}: {
  readonly gameType: string;
  readonly docs: GameDocLinks | null;
}) {
  if (!docs) return null;

  return (
    <div className="flex flex-wrap gap-1.5 border-border-subtle border-t bg-surface-primary px-4 py-2">
      {DOC_AXES.map((axis) => {
        const href = docs[axis.key];
        if (!href) return null;
        return (
          <a
            key={axis.key}
            href={href}
            data-testid={`game-docs-link-${axis.key}-${gameType}`}
            aria-label={`${gameType} ${axis.label} 문서 열기`}
            className="inline-flex min-h-8 cursor-pointer items-center gap-1 rounded-md border border-border-primary bg-surface-secondary px-2.5 font-medium text-text-secondary text-xs transition-colors hover:border-accent-blue hover:text-accent-blue"
          >
            <span aria-hidden="true">{axis.mark}</span>
            <span>{axis.label}</span>
          </a>
        );
      })}
    </div>
  );
}

function GameEntrySummary({
  entry,
  showDetails,
}: {
  readonly entry: CatalogEntry;
  readonly showDetails: boolean;
}) {
  const registryConfig = getGameType(entry.gameType);
  const icon = registryConfig?.icon ?? "□";
  const iconBg = registryConfig?.iconBg ?? "#eef2ff";
  const displayName = registryConfig?.displayName ?? entry.displayName;
  const manifest = entry.manifest;
  const description =
    registryConfig?.description ??
    (manifest
      ? `${manifest.minPlayers}~${manifest.maxPlayers} players`
      : "문서만 준비된 게임입니다");
  const addedOrder = registryConfig?.addedOrder ?? null;
  const supportsAiOpponent = registryConfig?.supportsAiOpponent === true;

  return (
    <>
      <div
        role="img"
        aria-label={`${displayName} 아이콘`}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl"
        style={{ background: iconBg }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-base">{displayName}</div>
            <div className="mt-0.5 text-text-muted text-xs">{description}</div>
          </div>
          {manifest ? (
            <div className="shrink-0 text-lg text-text-hint">›</div>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {manifest ? (
            <>
              <span
                data-testid={`game-player-count-${entry.gameType}`}
                className="inline-flex min-h-5 items-center rounded-full border border-accent-blue-light bg-accent-blue-bg px-2 font-bold text-[10px] text-accent-blue"
              >
                {formatPlayerCountLabel(manifest)}
              </span>
              <span
                data-testid={`game-observer-support-${entry.gameType}`}
                className="inline-flex min-h-5 items-center rounded-full border border-border-subtle bg-surface-primary px-2 font-bold text-[10px] text-text-secondary"
              >
                {formatObserverSupportLabel(manifest)}
              </span>
              {supportsAiOpponent ? (
                <span
                  data-testid={`game-ai-support-${entry.gameType}`}
                  className="inline-flex min-h-5 items-center rounded-full bg-accent-green px-2 font-bold text-[10px] text-white"
                >
                  AI 대전
                </span>
              ) : null}
            </>
          ) : (
            <span
              data-testid={`game-docs-only-badge-${entry.gameType}`}
              className="inline-flex min-h-5 items-center rounded-full border border-border-primary bg-surface-primary px-2 font-bold text-[10px] text-text-muted"
            >
              문서만 있음 · 플레이 준비 중
            </span>
          )}
        </div>
        {showDetails && manifest ? (
          <GameManifestDetails addedOrder={addedOrder} manifest={manifest} />
        ) : null}
      </div>
    </>
  );
}

function GameManifestDetails({
  addedOrder,
  manifest,
}: {
  readonly addedOrder: number | null;
  readonly manifest: GameManifest;
}) {
  const runtimePin = manifest.runtimePin;
  const deploymentDetails = [
    runtimePin ? "runtime pin 연결" : "runtime pin 없음",
    manifest.projectionPolicy,
    manifest.supportsObservers ? "관전 지원" : "관전 미지원",
  ].join(" · ");

  return (
    <dl className="mt-3 grid gap-2 border-border-subtle border-t pt-3 text-xs sm:grid-cols-2">
      <DetailItem label="규칙 버전">{formatRulesetLabel(manifest)}</DetailItem>
      <DetailItem label="런타임">{formatRuntimeLabel(manifest)}</DetailItem>
      <DetailItem label="엔진">
        {runtimePin
          ? `${runtimePin.enginePackage}@${runtimePin.engineVersion}`
          : "미할당"}
      </DetailItem>
      <DetailItem label="규칙 서비스">
        {runtimePin?.ruleServiceRef ?? "미할당"}
      </DetailItem>
      <DetailItem label="규칙 서비스 이미지">
        {runtimePin?.ruleServiceImage ?? "미할당"}
      </DetailItem>
      <DetailItem label="클라이언트 이미지">
        {runtimePin?.clientAppImage ?? "미할당"}
      </DetailItem>
      <DetailItem label="배포">{deploymentDetails}</DetailItem>
      <DetailItem label="등록 순서">
        {addedOrder === null ? "registry 없음" : `#${addedOrder}`}
      </DetailItem>
      <DetailItem label="로비 경로">
        {buildLobbyPath(manifest.gameType, "v2")}
      </DetailItem>
    </dl>
  );
}

function DetailItem({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="font-medium text-text-muted">{label}</dt>
      <dd className="mt-0.5 break-words text-text-secondary">{children}</dd>
    </div>
  );
}
